import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { CheckCircleIcon, CheckIcon } from '@heroicons/react/24/solid';
import {
  InformationCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ClipboardDocumentIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useCreateCandidate } from '../../hooks/useCandidates';
import { publicApi } from '../../utils/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import FileUpload from '../../components/ui/FileUpload';
import BrandHero from '../../components/brand/BrandHero';
import BrandBadge from '../../components/brand/BrandBadge';
import { SOURCES, NOTICE_UNITS } from '../../utils/constants';
import { applyServerFieldErrors, formatApiError } from '../../utils/errors';

const STEPS = ['Personal', 'Professional', 'Documents', 'Review'];

const STEP_FIELDS = [
  ['first_name', 'last_name', 'email', 'phone'],
  [
    'department', 'position', 'current_title', 'employer', 'current_salary',
    'expected_salary', 'notice_period', 'notice_unit', 'portfolio_url', 'github_url', 'source',
  ],
  ['cover_letter'],
  [],
];

const FORM_FIELDS = STEP_FIELDS.flat();
const CV_STEP = 2;
const REVIEW_STEP = 3;
const DRAFT_KEY = 'gc-application-draft';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[+()\-\s\d]{7,20}$/;
/** Deliberately looser than type="url" so `github.com/me` passes here, not in a native bubble. */
const LINK_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i;
const CV_MISSING_MESSAGE = 'Attach your CV to continue — PDF, DOC or DOCX, up to 10MB.';
const EMPTY = '—';
const COVER_LETTER_EXCERPT = 180;

const HERO_POINTS = [
  { icon: ClockIcon, text: 'About five minutes, and we keep what you type if you refresh.' },
  { icon: DocumentTextIcon, text: 'Bring your CV as a PDF, DOC or DOCX, up to 10MB.' },
  { icon: EnvelopeIcon, text: 'You get an HIQ tracking code by email as soon as you submit.' },
];

/** Text fields survive a refresh; the File object cannot be serialized. */
function loadDraft() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null');
    if (!parsed || typeof parsed !== 'object') return null;
    return Object.values(parsed).some(v => v) ? parsed : null;
  } catch {
    return null;
  }
}

function clearDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* private mode / quota — nothing to clean up */
  }
}

function formatAmount(value) {
  const amount = Number(value);
  return value !== '' && value != null && Number.isFinite(amount) ? amount.toLocaleString() : EMPTY;
}

function formatSize(bytes) {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Shared shell for every state of the flow (form, submitted, already applied) so
 * the brand panel and heading rhythm do not drift between them.
 */
function ApplyLayout({ testId, heroTitle, heroSubtitle, heroExtra, heading, subheading, children }) {
  return (
    // The brand track is capped rather than proportional: its copy is `max-w-md`,
    // so a wider share only bought it empty space the form column needed.
    <div
      className="min-h-dvh grid lg:grid-cols-[minmax(24rem,32rem)_minmax(0,1fr)] bg-slate-50"
      data-testid={testId}
    >
      <BrandHero
        sticky
        // The single <h1> belongs to the form column: this panel is display:none
        // below `lg`, so a heading here would vanish on a phone.
        titleAs="p"
        // my-auto shares the leftover height with the footer's own auto margin,
        // so a short window reads as deliberate spacing rather than a void.
        contentClassName="max-w-md my-auto"
        title={heroTitle}
        subtitle={heroSubtitle}
        footer={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link to="/careers" className="underline-offset-2 hover:text-white hover:underline focus-ring rounded">
              Browse all roles
            </Link>
            <Link to="/track" className="underline-offset-2 hover:text-white hover:underline focus-ring rounded">
              Track an application
            </Link>
            <span>Genesis Creations Talent Hub</span>
          </span>
        }
      >
        {heroExtra}
      </BrandHero>

      <div className="flex flex-col px-4 py-10 sm:py-12 md:px-8">
        {/* my-auto centres a short step against the full-height panel instead of
            stranding it at the top with a void underneath. */}
        <div className="w-full max-w-xl md:max-w-2xl lg:max-w-xl xl:max-w-2xl mx-auto my-auto">
          <div className="text-center lg:text-left">
            <BrandBadge className="lg:hidden mb-5 sm:mb-6" />
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{heading}</h1>
            {subheading && <p className="text-sm text-slate-600 mt-2">{subheading}</p>}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Grouping for the eleven professional fields, which are unreadable as one stack.
 *
 * The rule and its padding sit on a wrapper rather than the fieldset because a
 * browser lays a <legend> out above the fieldset's padding box: with padding there,
 * the title lands hard against the rule with the whole gap below it instead.
 *
 * It divides below rather than above so `last:` can switch it off. Above, it would
 * need `first:`, and the first section is not its parent's first child — the step
 * heading is, which is why the old `first:border-0` never once matched.
 */
function FormSection({ title, children }) {
  return (
    <div className="border-b border-slate-200 pb-6 last:border-0 last:pb-0">
      <fieldset>
        {/* A third weight of slate-900 semibold inside one card made group titles
            read as extra field labels; a different register separates them. */}
        <legend className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</legend>
        <div className="space-y-4">{children}</div>
      </fieldset>
    </div>
  );
}

function HeroPoints({ className }) {
  return (
    <ul className={clsx('mt-8 space-y-3 border-t border-white/15 pt-6', className)}>
      {HERO_POINTS.map(({ icon: Icon, text }) => (
        <li key={text} className="flex gap-3 text-sm text-brand-100">
          <Icon className="h-5 w-5 flex-shrink-0 text-brand-200" aria-hidden="true" />
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ApplicationForm() {
  const [initialDraft] = useState(loadDraft);
  const [step, setStep] = useState(0);
  const [cvFile, setCvFile] = useState(null);
  const [cvError, setCvError] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [duplicate, setDuplicate] = useState(false);
  const [draftNotice, setDraftNotice] = useState(() => !!initialDraft);
  const [departments, setDepartments] = useState([]);
  const [deptLoading, setDeptLoading] = useState(true);
  const [deptError, setDeptError] = useState(false);
  const [serverNotice, setServerNotice] = useState(null);
  const [copied, setCopied] = useState(false);
  const stepHeadingRef = useRef(null);
  const formRef = useRef(null);
  const cvRef = useRef(null);
  const copyTimer = useRef(null);
  const createMut = useCreateCandidate();
  const [searchParams] = useSearchParams();
  const requestedDepartment = searchParams.get('department');
  const requestedPosition = searchParams.get('position');
  const {
    register, handleSubmit, watch, setError, trigger, setValue, setFocus, getValues,
    formState: { errors, isDirty },
  } = useForm({
    // Without these, `trigger()` on a wizard step raises errors that never clear:
    // `isSubmitted` stays false on a step, so the default mode skips revalidation
    // on change and a corrected field keeps its red message until the next click.
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: initialDraft || undefined,
  });

  const loadDepartments = useCallback(() => {
    setDeptLoading(true);
    setDeptError(false);
    publicApi.get('/settings/departments/public')
      .then(r => setDepartments(r.data?.departments || []))
      .catch(() => setDeptError(true))
      .finally(() => setDeptLoading(false));
  }, []);

  useEffect(() => { loadDepartments(); }, [loadDepartments]);

  // Snapshot the text fields so a refresh or back gesture does not wipe the form.
  useEffect(() => {
    const sub = watch(values => {
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(values));
      } catch {
        /* private mode / quota — draft is best-effort */
      }
    });
    return () => sub.unsubscribe();
  }, [watch]);

  // A summary that outlives the problem it described is worse than no summary: the
  // fields clear themselves as they are corrected, so this has to go with them.
  useEffect(() => {
    if (!serverNotice) return undefined;
    const sub = watch(() => setServerNotice(null));
    return () => sub.unsubscribe();
  }, [watch, serverNotice]);

  const hasUnsavedWork = !submitted && (isDirty || !!cvFile);
  useEffect(() => {
    if (!hasUnsavedWork) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedWork]);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const watchDept = watch('department');
  const watchPosition = watch('position');
  const positions = useMemo(
    () => departments.find(d => d.name === watchDept)?.positions || [],
    [departments, watchDept]
  );

  // Both selects register before their options exist, so a value coming from the
  // careers link or the restored draft has to be re-applied once the options are
  // on the page — otherwise the form state holds a role the <select> never shows.
  // A role picked from /careers wins over the draft; everything else is kept.
  useEffect(() => {
    if (!departments.length) return;
    const linked = departments.some(d => d.name === requestedDepartment) ? requestedDepartment : null;
    const wanted = linked || getValues('department');
    if (wanted) setValue('department', wanted);
  }, [departments, requestedDepartment, getValues, setValue]);

  useEffect(() => {
    if (!positions.length) return;
    const linked = positions.some(p => p.title === requestedPosition) ? requestedPosition : null;
    const wanted = linked || getValues('position');
    if (wanted && positions.some(p => p.title === wanted)) setValue('position', wanted);
  }, [positions, requestedPosition, getValues, setValue]);

  // Land on the section, not on its first field: the heading names what changed,
  // and a screen reader would otherwise hear an input with no context.
  const movedRef = useRef(false);
  useEffect(() => {
    if (!movedRef.current) {
      movedRef.current = true;
      return;
    }
    // Leaving the eleven-field step shrinks the card by ~540px, and the browser
    // then clamps scroll by the same amount: half a viewport of lurch that nothing
    // on screen explains. Land on the card top on purpose, instantly — a smooth
    // scroll here would run past the focus move and cannot be turned off by the
    // reduced-motion block, which only reaches the CSS property.
    const card = formRef.current;
    if (card) {
      const top = card.getBoundingClientRect().top + window.scrollY - 16;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    }
    stepHeadingRef.current?.focus({ preventScroll: true });
  }, [step]);

  const roleLabel = watchPosition && watchDept
    ? `${watchPosition}, ${watchDept}`
    : watchDept || watchPosition || null;

  const selectCv = (file) => {
    setCvFile(file);
    if (file) setCvError(null);
  };

  const goToStep = (target) => {
    setServerNotice(null);
    setStep(target);
  };

  const nextStep = async () => {
    // Field by field rather than as a batch: `errors` in this closure is the
    // pre-trigger render's snapshot, so the per-field results are what can be
    // trusted to name the first thing to fix.
    const results = await Promise.all(STEP_FIELDS[step].map(field => trigger(field)));
    const failing = STEP_FIELDS[step].filter((_, i) => !results[i]);
    // Checked in the same pass: a Documents step with no CV and no cover letter
    // used to report the letter, then the CV only on the second attempt.
    const cvMissing = step === CV_STEP && !cvFile;
    if (cvMissing) setCvError(CV_MISSING_MESSAGE);
    if (!failing.length && !cvMissing) {
      goToStep(step + 1);
      return;
    }

    // One summary instead of up to eleven simultaneous alerts, and focus lands on
    // the first thing to fix — leaving it on Next makes the button look broken.
    const count = failing.length + (cvMissing ? 1 : 0);
    setServerNotice({
      title: count === 1 ? 'One answer needs your attention' : `${count} answers need your attention`,
      message: 'We marked them below.',
    });
    if (failing.length) setFocus(failing[0]);
    else if (cvMissing) cvRef.current?.focus();
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(submitted.code);
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Copying is blocked in this browser — select the code and copy it manually.');
    }
  };

  const onSubmit = (data) => {
    setServerNotice(null);
    if (!cvFile) {
      setCvError(CV_MISSING_MESSAGE);
      setStep(CV_STEP);
      return;
    }
    const formData = new FormData();
    Object.entries(data).forEach(([k, v]) => { if (v) formData.append(k, v); });
    formData.append('cv', cvFile);

    createMut.mutate(formData, {
      onSuccess: (res) => {
        clearDraft();
        setSubmitted({ code: res.app_code || res.candidate?.app_code || null });
      },
      onError: (err) => {
        // A returning candidate gets a recovery path, not a dead-end toast.
        if (err.response?.status === 409) {
          setDuplicate(true);
          return;
        }

        const applied = applyServerFieldErrors(err, setError, { fields: FORM_FIELDS });
        if (applied.length) {
          const failingStep = STEP_FIELDS.findIndex(fields => fields.some(f => applied.includes(f)));
          if (failingStep >= 0) setStep(failingStep);
          const notice = applied.length === 1
            ? 'One field needs fixing — we highlighted it for you.'
            : `${applied.length} fields need fixing — we highlighted them for you.`;
          setServerNotice({ title: 'We could not submit your application', message: notice });
          toast.error(notice);
          return;
        }

        // CV problems (size, type, missing) belong on the Documents step, not on Review.
        const message = formatApiError(err, 'We could not submit your application. Please try again.');
        if (err.response?.status === 413 || /\b(cv|file|resume)\b/i.test(message)) {
          setCvError(message);
          setStep(CV_STEP);
        }
        setServerNotice({ title: 'We could not submit your application', message });
        toast.error(message);
      },
    });
  };

  /**
   * Enter inside any field submits a form natively, and this one is only ready
   * on the Review step — anywhere else that keypress would send a half-finished
   * application, so it advances instead.
   */
  // An error on a field belonging to an earlier step lands on an unmounted input,
  // so without this branch Submit silently does nothing at all.
  const onInvalid = (invalidFields) => {
    const failingStep = STEP_FIELDS.findIndex(fields => fields.some(f => invalidFields[f]));
    if (failingStep >= 0) setStep(failingStep);
    setServerNotice({
      title: 'Something on an earlier step needs fixing',
      message: 'We took you back to it and marked what to change.',
    });
  };

  const submitOrAdvance = (e) => {
    if (step !== REVIEW_STEP) {
      e.preventDefault();
      nextStep();
      return;
    }
    handleSubmit(onSubmit, onInvalid)(e);
  };

  if (submitted) {
    return (
      <ApplyLayout
        testId="apply-success"
        heroTitle="That is with us now."
        heroSubtitle="A recruiter reads every application. You will hear from us by email, and your HIQ code shows where things stand at any time."
        heroExtra={<HeroPoints />}
        heading="Application submitted"
        subheading="Thank you for applying to Genesis Creations. Here is what happens next."
      >
        <div className="surface-elevated p-6 sm:p-8 mt-6">
          <CheckCircleIcon className="h-12 w-12 text-success-500 mx-auto" aria-hidden="true" />
          <p className="text-center text-slate-600 mt-3">
            We have your application and your CV.
          </p>

          {submitted.code && (
            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Your application code</p>
              <p className="text-xl font-mono font-bold tracking-wider text-brand-600 mt-1" data-testid="apply-code">
                {submitted.code}
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-3"
                onClick={copyCode}
                data-testid="apply-copy-code"
              >
                {copied
                  ? <><CheckIcon className="h-4 w-4" aria-hidden="true" />Copied</>
                  : <><ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />Copy code</>}
              </Button>
              <p aria-live="polite" className="sr-only">{copied ? 'Application code copied' : ''}</p>
            </div>
          )}

          <ol className="mt-6 space-y-3 text-sm text-slate-600">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700">1</span>
              <span>A confirmation email is on its way, with the same code.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700">2</span>
              <span>Our recruiters review your CV against the role.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700">3</span>
              <span>If it is a match, we email you to arrange a first conversation.</span>
            </li>
          </ol>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/track" className="sm:flex-1">
              <Button className="w-full">Track your application</Button>
            </Link>
            <Link to="/careers" className="sm:flex-1">
              <Button variant="secondary" className="w-full">Browse more roles</Button>
            </Link>
          </div>
        </div>
      </ApplyLayout>
    );
  }

  if (duplicate) {
    return (
      <ApplyLayout
        testId="apply-duplicate"
        heroTitle="We already have you on file."
        heroSubtitle="One application per email address is enough — a second one would not move you up the list."
        heroExtra={<HeroPoints />}
        heading="You have already applied"
        subheading="There is nothing more to submit for this email address."
      >
        <div className="surface-elevated p-6 sm:p-8 mt-6">
          <p className="text-sm text-slate-600">
            Your confirmation email has the HIQ code you need to check where things stand. If you cannot find it,
            the candidate portal can send you a fresh sign-in link.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <Link to="/track">
              <Button className="w-full">Track your application</Button>
            </Link>
            <Link to="/candidate/login">
              <Button variant="secondary" className="w-full">Sign in to the candidate portal</Button>
            </Link>
          </div>
          <p className="text-xs text-slate-500 mt-5">
            Applying with a different email?{' '}
            <button
              type="button"
              onClick={() => setDuplicate(false)}
              className="font-medium text-brand-600 hover:underline focus-ring rounded"
            >
              Go back to the form
            </button>
          </p>
        </div>
      </ApplyLayout>
    );
  }

  const reviewValues = step === REVIEW_STEP ? getValues() : null;
  const coverLetter = reviewValues?.cover_letter || '';
  const reviewSections = reviewValues ? [
    {
      step: 0,
      title: 'Personal',
      rows: [
        ['Name', [reviewValues.first_name, reviewValues.last_name].filter(Boolean).join(' ') || EMPTY],
        ['Email', reviewValues.email || EMPTY],
        ['Phone', reviewValues.phone || EMPTY],
      ],
    },
    {
      step: 1,
      title: 'Professional',
      rows: [
        ['Department', reviewValues.department || EMPTY],
        ['Position', reviewValues.position || EMPTY],
        ['Current title', reviewValues.current_title || EMPTY],
        ['Current employer', reviewValues.employer || EMPTY],
        ['Current salary', formatAmount(reviewValues.current_salary)],
        ['Expected salary', formatAmount(reviewValues.expected_salary)],
        ['Notice period', reviewValues.notice_period
          ? `${reviewValues.notice_period} ${reviewValues.notice_unit || ''}`.trim()
          : EMPTY],
        ['Portfolio', reviewValues.portfolio_url || EMPTY],
        ['GitHub', reviewValues.github_url || EMPTY],
        ['Found us via', reviewValues.source || EMPTY],
      ],
    },
    {
      step: CV_STEP,
      title: 'Documents',
      rows: [
        ['CV', cvFile ? `${cvFile.name} (${formatSize(cvFile.size)})` : 'Not uploaded'],
        ['Cover letter', coverLetter
          ? (coverLetter.length > COVER_LETTER_EXCERPT
            ? `${coverLetter.slice(0, COVER_LETTER_EXCERPT).trimEnd()}…`
            : coverLetter)
          : EMPTY],
      ],
    },
  ] : [];

  return (
    <ApplyLayout
      testId="apply-page"
      heroTitle="Build what comes next with us."
      heroSubtitle="Genesis Creations hires for craft and curiosity. Tell us how you work and what you have shipped — the form is short and your progress is kept as you go."
      heroExtra={
        <>
          {/* Both blocks below are hidden on windows too short to hold them: the
              panel cannot scroll, and the same role is named in the subheading. */}
          {roleLabel && (
            <p className="mt-6 hidden mid:inline-flex max-w-full items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-xs font-medium text-white">
              <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent-300" aria-hidden="true" />
              <span className="truncate">Applying for {roleLabel}</span>
            </p>
          )}
          <HeroPoints className="hidden tall:block" />
        </>
      }
      heading="Apply to Genesis Creations"
      subheading={roleLabel
        ? `You are applying for ${roleLabel}.`
        : 'Four short steps. You pick the role you want on the Professional step.'}
    >
      <form ref={formRef} onSubmit={submitOrAdvance} noValidate className="mt-6" data-testid="apply-form">
        {/* One card, not a ladder of panels: progress is this card's header and the
            controls are its footer, so nothing floats beside the thing it describes. */}
        <div className="surface-elevated p-6 sm:p-8">
          <nav
            aria-label="Application progress"
            className="-mx-6 -mt-6 mb-6 border-b border-slate-200 px-6 py-4 sm:-mx-8 sm:-mt-8 sm:mb-8 sm:px-8"
          >
            <p className="text-xs font-medium text-slate-500">Step {step + 1} of {STEPS.length}</p>
            <ol className="mt-2.5 flex gap-1.5">
              {STEPS.map((label, i) => {
                const bar = (
                  <span
                    className={clsx(
                      'block h-1.5 rounded-full transition-colors duration-200 ease-out',
                      i === step && 'bg-brand-600',
                      i < step && 'bg-brand-400 group-hover:bg-brand-600',
                      i > step && 'bg-slate-200'
                    )}
                  />
                );
                const text = (
                  <span
                    className={clsx(
                      'mt-1.5 block truncate text-xs transition-colors duration-200 ease-out',
                      i === step && 'font-semibold text-brand-700',
                      i < step && 'text-brand-600 group-hover:text-brand-800 group-hover:underline',
                      i > step && 'text-slate-500'
                    )}
                  >
                    {label}
                  </span>
                );
                return (
                  <li key={label} className="flex-1 min-w-0">
                    {i < step ? (
                      // -my-2 keeps the 44px target without spending vertical rhythm on it.
                      <button
                        type="button"
                        onClick={() => goToStep(i)}
                        aria-label={`Go back to step ${i + 1}, ${label}`}
                        className="group -my-2 block w-full rounded-lg px-1 py-2 focus-ring hover:bg-brand-50"
                      >
                        {bar}
                        {text}
                      </button>
                    ) : (
                      <div className="px-1" aria-current={i === step ? 'step' : undefined}>
                        {bar}
                        {text}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>

          {serverNotice && (
            <div role="alert" className="mb-5 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3.5">
              <p className="text-sm font-semibold text-danger-800">{serverNotice.title}</p>
              <p className="mt-1 text-sm text-danger-700">{serverNotice.message}</p>
            </div>
          )}

          {draftNotice && (
            <div className="mb-5 flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
              <InformationCircleIcon className="h-5 w-5 text-brand-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1 text-sm text-brand-900">
                <p className="font-medium">We restored what you had typed.</p>
                <p className="mt-0.5">Your CV was not saved — please select the file again on the Documents step.</p>
              </div>
              <button
                type="button"
                onClick={() => setDraftNotice(false)}
                aria-label="Dismiss"
                className="-mr-1 -mt-1 rounded-lg p-1.5 text-brand-700 transition-colors hover:bg-brand-100 hover:text-brand-800 focus-ring"
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}

          {step === 0 && (
            <div className="space-y-6 motion-safe:animate-step-in" data-testid="apply-step-personal">
              <h2 ref={stepHeadingRef} tabIndex={-1} className="text-xl font-semibold tracking-tight text-slate-900 focus:outline-none">
                Personal Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  required
                  autoComplete="given-name"
                  data-testid="apply-first-name"
                  error={errors.first_name?.message}
                  {...register('first_name', { required: 'Enter your first name' })}
                />
                <Input
                  label="Last Name"
                  required
                  autoComplete="family-name"
                  data-testid="apply-last-name"
                  error={errors.last_name?.message}
                  {...register('last_name', { required: 'Enter your last name' })}
                />
              </div>
              <Input
                label="Email"
                required
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@email.com"
                hint="This is where your confirmation and tracking code go."
                data-testid="apply-email"
                error={errors.email?.message}
                {...register('email', {
                  required: 'Enter the email we should reply to',
                  pattern: { value: EMAIL_PATTERN, message: 'Enter a valid email address, like you@email.com' },
                })}
              />
              <Input
                label="Phone"
                required
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                data-testid="apply-phone"
                error={errors.phone?.message}
                {...register('phone', {
                  required: 'Enter a phone number we can reach you on',
                  pattern: { value: PHONE_PATTERN, message: 'Enter a valid phone number, digits and + only' },
                })}
              />
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6 motion-safe:animate-step-in" data-testid="apply-step-professional">
              <h2 ref={stepHeadingRef} tabIndex={-1} className="text-xl font-semibold tracking-tight text-slate-900 focus:outline-none">
                Professional Information
              </h2>

              {deptError && (
                <div role="alert" className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3">
                  <p className="text-sm text-danger-800">
                    We could not load the open roles, so the two menus below are empty.
                  </p>
                  <button
                    type="button"
                    onClick={loadDepartments}
                    className="mt-1 text-sm font-medium text-danger-800 underline hover:text-danger-900 focus-ring rounded"
                  >
                    Try again
                  </button>
                </div>
              )}

              <FormSection title="Role">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Select
                    label="Department"
                    required
                    options={departments.map(d => d.name)}
                    placeholder={deptLoading ? 'Loading roles...' : 'Select'}
                    data-testid="apply-department"
                    error={errors.department?.message}
                    {...register('department', { required: 'Choose the department you are applying to' })}
                  />
                  <Select
                    label="Position"
                    required
                    options={positions.map(p => p.title)}
                    placeholder={watchDept ? 'Select' : 'Choose a department first'}
                    data-testid="apply-position"
                    error={errors.position?.message}
                    {...register('position', { required: 'Choose the role you are applying for' })}
                  />
                </div>
              </FormSection>

              <FormSection title="Current role">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Current Title"
                    required
                    autoComplete="organization-title"
                    error={errors.current_title?.message}
                    {...register('current_title', { required: 'Enter your current job title' })}
                  />
                  <Input
                    label="Current Employer"
                    required
                    autoComplete="organization"
                    error={errors.employer?.message}
                    {...register('employer', { required: 'Enter your current employer' })}
                  />
                </div>
              </FormSection>

              <FormSection title="Compensation and availability">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Current Salary"
                    required
                    type="number"
                    inputMode="numeric"
                    min={0}
                    hint="Monthly, gross, in EGP."
                    error={errors.current_salary?.message}
                    {...register('current_salary', {
                      required: 'Enter your current monthly salary',
                      min: { value: 0, message: 'Salary cannot be negative' },
                    })}
                  />
                  <Input
                    label="Expected Salary"
                    required
                    type="number"
                    inputMode="numeric"
                    min={0}
                    hint="Monthly, gross, in EGP."
                    error={errors.expected_salary?.message}
                    {...register('expected_salary', {
                      required: 'Enter the salary you are looking for',
                      min: { value: 0, message: 'Salary cannot be negative' },
                    })}
                  />
                </div>
                {/* The unit menu needs a fraction of the width the number does. */}
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_9rem] gap-4">
                  <Input
                    label="Notice Period"
                    required
                    type="number"
                    inputMode="numeric"
                    min={0}
                    hint="How long before you could start."
                    error={errors.notice_period?.message}
                    {...register('notice_period', {
                      required: 'Enter how much notice you must give',
                      min: { value: 0, message: 'Notice period cannot be negative' },
                    })}
                  />
                  <Select
                    label="Unit"
                    required
                    options={NOTICE_UNITS}
                    error={errors.notice_unit?.message}
                    {...register('notice_unit', { required: 'Choose days, weeks or months' })}
                  />
                </div>
              </FormSection>

              <FormSection title="Portfolio and referral">
                <Input
                  label="Portfolio URL"
                  required
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  error={errors.portfolio_url?.message}
                  placeholder="https://yourportfolio.com"
                  {...register('portfolio_url', {
                    required: 'Add a link to your portfolio or personal site',
                    pattern: { value: LINK_PATTERN, message: 'Enter a link like yourportfolio.com or https://yourportfolio.com' },
                  })}
                />
                <Input
                  label="GitHub URL"
                  required
                  type="text"
                  inputMode="url"
                  error={errors.github_url?.message}
                  placeholder="https://github.com/username"
                  {...register('github_url', {
                    required: 'Add a link to your GitHub profile',
                    pattern: { value: LINK_PATTERN, message: 'Enter a link like github.com/username' },
                  })}
                />
                <Select
                  label="How did you find us?"
                  required
                  options={SOURCES}
                  placeholder="Select"
                  error={errors.source?.message}
                  {...register('source', { required: 'Tell us where you heard about the role' })}
                />
              </FormSection>
            </div>
          )}

          {step === CV_STEP && (
            <div className="space-y-6 motion-safe:animate-step-in" data-testid="apply-step-documents">
              <h2 ref={stepHeadingRef} tabIndex={-1} className="text-xl font-semibold tracking-tight text-slate-900 focus:outline-none">
                Documents
              </h2>
              <FileUpload ref={cvRef} label="Upload CV" required value={cvFile} onChange={selectCv} error={cvError} />
              <Textarea
                label="Cover Letter"
                required
                rows={5}
                placeholder="Tell us why you'd be a great fit..."
                hint="A short paragraph is plenty — what you want to work on and why here."
                error={errors.cover_letter?.message}
                {...register('cover_letter', {
                  required: 'Tell us briefly why you are a good fit — this is required',
                })}
              />
            </div>
          )}

          {step === REVIEW_STEP && (
            <div className="space-y-6 motion-safe:animate-step-in" data-testid="apply-step-review">
              <h2 ref={stepHeadingRef} tabIndex={-1} className="text-xl font-semibold tracking-tight text-slate-900 focus:outline-none">
                Review &amp; Submit
              </h2>
              <p className="text-sm text-slate-600">
                Check everything below, then submit. You cannot edit an application after it is sent.
              </p>

              {reviewSections.map(section => (
                <div
                  key={section.title}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-5 transition-colors hover:border-brand-200 hover:bg-white focus-within:border-brand-300 focus-within:bg-white"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
                    {/* A chip, not bare text: as `text-brand-600` on this tile it sat at
                        1.27:1 against the values below it and was a 24x20px target. */}
                    <button
                      type="button"
                      onClick={() => goToStep(section.step)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-brand-700 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-800 active:bg-brand-100 focus-ring"
                    >
                      <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      Edit<span className="sr-only"> {section.title}</span>
                    </button>
                  </div>
                  {/* A shared column rather than per-row flex, which started each
                      value at a different x and left ten rows with a ragged edge. */}
                  <dl className="mt-4 text-sm sm:grid sm:grid-cols-[9rem_1fr] sm:gap-x-4 sm:gap-y-2">
                    {section.rows.map(([label, value]) => (
                      <div key={label} className="mt-2 first:mt-0 sm:contents">
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 sm:pt-0.5">
                          {label}
                        </dt>
                        <dd className="min-w-0 break-words text-slate-800">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}

              {!cvFile && (
                <div role="alert" className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3">
                  <p className="text-sm text-danger-800">{CV_MISSING_MESSAGE}</p>
                  <button
                    type="button"
                    onClick={() => goToStep(CV_STEP)}
                    className="mt-1 text-sm font-medium text-danger-800 underline hover:text-danger-900 focus-ring rounded"
                  >
                    Go back to Documents
                  </button>
                </div>
              )}

              <p className="max-w-prose text-xs leading-relaxed text-slate-500">
                We use these details only to assess your application. Your CV is shared with the hiring team for this
                role and stored with your candidate record.
              </p>
            </div>
          )}

          {/* The card's own footer. Sticky inside the card means it rests under the
              last field on a short step and pins to the window on the long one,
              instead of floating in the dead space below a detached pill. */}
          <div className="sticky bottom-0 z-raised -mx-6 -mb-6 mt-8 flex items-center justify-end gap-3 rounded-b-2xl border-t border-slate-200 bg-white/95 px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur sm:-mx-8 sm:-mb-8 sm:px-8">
            {step > 0 && (
              <Button
                type="button"
                variant="secondary"
                className="mr-auto"
                onClick={() => goToStep(step - 1)}
                data-testid="apply-back"
              >
                Back
              </Button>
            )}
            {/* Distinct keys keep these as two DOM nodes. Sharing one means the
                click that leaves the Documents step mutates the node it is still
                being dispatched on into a submit button, and the browser then
                submits the form as that click's default action. */}
            {step < REVIEW_STEP ? (
              <Button key="next" type="button" onClick={nextStep} data-testid="apply-next">Next</Button>
            ) : (
              <Button key="submit" type="submit" loading={createMut.isLoading} data-testid="apply-submit">
                Submit Application
              </Button>
            )}
          </div>
        </div>
      </form>
    </ApplyLayout>
  );
}
